import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Save, X, Settings
  // Upload, Download, FileText, AlertTriangle, CheckCircle, Info - unused icons
} from 'lucide-react';
import api from '../../services/api';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import usePermissions from '../../hooks/usePermissions';

const UploadProfiles = () => {
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('upload-profiles', 'delete');
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    vendor: '',
    format: 'excel',
    columnMappings: {
      itemCode: '',
      name: '',
      category: '',
      goldWeight: '',
      goldPurity: '',
      goldRate: '',
      diamondWeight: '',
      diamondRate: '',
      stoneWeight: '',
      stoneRate: '',
      laborCost: '',
      otherCosts: '',
      salePrice: '',
      vendor: '',
      notes: ''
    },
    settings: {
      skipRows: 0,
      hasHeaders: true,
      dateFormat: 'MM/DD/YYYY',
      currency: 'USD'
    }
  });

  useEffect(() => {
    loadProfiles();
  }, []);

  // Load upload profiles
  const loadProfiles = async () => {
    try {
      setIsLoading(true);
      const response = await api.getUploadProfiles();
      setProfiles(response || []);
    } catch (error) {
      console.error('Failed to load upload profiles:', error);
      // Note: Profiles will be empty if API fails - database-first approach
    } finally {
      setIsLoading(false);
    }
  };

  // Save or update profile
  const saveProfile = async () => {
    try {
      if (editingProfile) {
        // Update existing profile
        const response = await api.updateUploadProfile(editingProfile.id, formData);
        setProfiles(profiles.map(p => p.id === editingProfile.id ? response : p));
      } else {
        // Create new profile
        const response = await api.createUploadProfile(formData);
        setProfiles([...profiles, response]);
      }
      
      resetForm();
      alert('Profile saved successfully!');
    } catch (error) {
      console.error('Failed to save profile:', error);
      alert('Failed to save profile. Please try again.');
    }
  };

  // Delete profile
  const deleteProfile = async (profileId) => {
    if (!canDelete) return;
    if (!window.confirm('Are you sure you want to delete this profile?')) {
      return;
    }
    
    try {
      await api.deleteUploadProfile(profileId);
      const updatedProfiles = profiles.filter(p => p.id !== profileId);
      setProfiles(updatedProfiles);
      alert('Profile deleted successfully!');
    } catch (error) {
      console.error('Failed to delete profile:', error);
      alert('Failed to delete profile. Please try again.');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      vendor: '',
      format: 'excel',
      columnMappings: {
        itemCode: '',
        name: '',
        category: '',
        goldWeight: '',
        goldPurity: '',
        goldRate: '',
        diamondWeight: '',
        diamondRate: '',
        stoneWeight: '',
        stoneRate: '',
        laborCost: '',
        otherCosts: '',
        salePrice: '',
        vendor: '',
        notes: ''
      },
      settings: {
        skipRows: 0,
        hasHeaders: true,
        dateFormat: 'MM/DD/YYYY',
        currency: 'USD'
      }
    });
    setEditingProfile(null);
    setShowModal(false);
  };

  // Edit profile
  const editProfile = (profile) => {
    setFormData({
      name: profile.name || '',
      description: profile.description || '',
      vendor: profile.vendor || '',
      format: profile.format || 'excel',
      columnMappings: profile.columnMappings || {
        itemCode: '',
        name: '',
        category: '',
        goldWeight: '',
        goldPurity: '',
        goldRate: '',
        diamondWeight: '',
        diamondRate: '',
        stoneWeight: '',
        stoneRate: '',
        laborCost: '',
        otherCosts: '',
        salePrice: '',
        vendor: '',
        notes: ''
      },
      settings: profile.settings || {
        skipRows: 0,
        hasHeaders: true,
        dateFormat: 'MM/DD/YYYY',
        currency: 'USD'
      }
    });
    setEditingProfile(profile);
    setShowModal(true);
  };

  // Handle input changes
  const handleInputChange = (field, value, section = null) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  return (
    <div className="p-6 pb-12">
      <PageIdentifier pageId={SCREEN_IDS?.ADMINISTRATION?.UPLOAD_PROFILES || 'ADM-006'} pageName="Upload Profiles" />
      
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Upload Profiles</h2>
        <p className="text-gray-600">
          Manage vendor-specific upload configurations for bulk inventory imports
        </p>
      </div>

      {/* Action Bar */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex gap-3">
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
          >
            <Plus size={18} />
            New Profile
          </button>
        </div>
      </div>

      {/* Profiles List */}
      <div className="bg-white rounded-lg shadow-md">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading profiles...</p>
          </div>
        ) : profiles.length === 0 ? (
          <div className="p-12 text-center">
            <Settings className="mx-auto mb-4 text-gray-300" size={64} />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No upload profiles found</h3>
            <p className="text-gray-600 mb-4">
              Create upload profiles to configure vendor-specific file formats
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Create First Profile
            </button>
          </div>
        ) : (
          <div className="divide-y">
            {profiles.map((profile) => (
              <div key={profile.id} className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">{profile.name}</h3>
                    {profile.description && (
                      <p className="text-gray-600 mt-1">{profile.description}</p>
                    )}
                    <div className="flex gap-4 mt-2 text-sm text-gray-500">
                      <span>Vendor: {profile.vendor || 'Not specified'}</span>
                      <span>Format: {profile.format || 'excel'}</span>
                      <span>Created: {new Date(profile.createdAt || profile.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => editProfile(profile)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit Profile"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => deleteProfile(profile.id)}
                      className={`p-2 rounded ${canDelete ? 'text-red-600 hover:bg-red-50' : 'text-gray-300 cursor-not-allowed'}`}
                      title={canDelete ? 'Delete Profile' : 'You do not have permission to delete profiles'}
                      disabled={!canDelete}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                
                {/* Column Mappings Preview */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Column Mappings:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
                    {Object.entries(profile.columnMappings || {})
                      .filter(([_, value]) => value)
                      .slice(0, 8)
                      .map(([field, column]) => (
                        <span key={field} className="text-gray-600">
                          <strong>{field}:</strong> {column}
                        </span>
                      ))}
                    {Object.entries(profile.columnMappings || {}).filter(([_, value]) => value).length > 8 && (
                      <span className="text-gray-500">
                        +{Object.entries(profile.columnMappings || {}).filter(([_, value]) => value).length - 8} more...
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profile Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">
                {editingProfile ? 'Edit Upload Profile' : 'Create Upload Profile'}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Profile Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Baanganga Excel Format"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Vendor</label>
                  <input
                    type="text"
                    value={formData.vendor}
                    onChange={(e) => handleInputChange('vendor', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Baanganga"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Brief description of this upload profile..."
                />
              </div>

              {/* Format Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">File Format</label>
                  <select
                    value={formData.format}
                    onChange={(e) => handleInputChange('format', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="excel">Excel (.xlsx, .xls)</option>
                    <option value="csv">CSV (.csv)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Currency</label>
                  <select
                    value={formData.settings.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value, 'settings')}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="INR">INR (₹)</option>
                  </select>
                </div>
              </div>

              {/* Column Mappings */}
              <div>
                <h4 className="text-lg font-medium mb-4">Column Mappings</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Map the columns from your vendor's file to our system fields
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(formData.columnMappings).map(([field, value]) => (
                    <div key={field}>
                      <label className="block text-sm font-medium mb-1 capitalize">
                        {field.replace(/([A-Z])/g, ' $1').trim()}
                      </label>
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => handleInputChange(field, e.target.value, 'columnMappings')}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder={`Column name for ${field}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Advanced Settings */}
              <div>
                <h4 className="text-lg font-medium mb-4">Advanced Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Skip Rows</label>
                    <input
                      type="number"
                      value={formData.settings.skipRows}
                      onChange={(e) => handleInputChange('skipRows', parseInt(e.target.value) || 0, 'settings')}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      min="0"
                      max="10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date Format</label>
                    <select
                      value={formData.settings.dateFormat}
                      onChange={(e) => handleInputChange('dateFormat', e.target.value, 'settings')}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center mt-6">
                      <input
                        type="checkbox"
                        checked={formData.settings.hasHeaders}
                        onChange={(e) => handleInputChange('hasHeaders', e.target.checked, 'settings')}
                        className="mr-2"
                      />
                      File has headers
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveProfile}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                disabled={!formData.name}
              >
                <Save size={18} />
                {editingProfile ? 'Update Profile' : 'Create Profile'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadProfiles;
